/**
 * @typedef {import('../../../App/shared/AgentService/Agent').default} Agent
 * @typedef {import('../../../shared/AgentContactService/AgentContact').default} AgentContact
 * @typedef {import('./ContactComponent').ContactsData} ContactsData
 * @typedef {import('./ContactComponent').Props} Props
 * @typedef {import('../../../types/AuthContext').default} Context
 * @typedef {import('./ContactComponent').State} State
 * @typedef {import('../../shared/Query/Filters/Component').QueryParameters} FilterQueryParameters
 * @typedef {import('../../shared/Query/Parameters').default} QueryParameters
 */
import { translate } from '../../Internationalization';
import {
	withAgentContactService,
	withExportService,
} from '../../../service-container';
import { withAuth } from '../../../shared/AuthProvider';
import { withSettings } from '../../../shared/SettingProvider/SettingProvider';
import arrayHasConsecutiveNumbers from '../../../utils/arrayHasConsecutiveNumbers';
import BaseButton from '../../../shared/BaseButton/BaseButton';
import captureError from '../../../utils/captureError';
import ContactsTable from '../shared/ContactsTable';
import Feature from '../../../shared/Feature';
import flags from '../../../shared/Flags';
import FormWarningMessages from '../../../shared/Forms/Messages/FormWarningMessages';
import images from '../../../config/local/images';
import LinkButton from '../../../shared/LinkButton';
import MoverScoreFilter from '../../shared/Query/Filters/MoverScore/MoverScoreFilter';
import NoContent from '../../../shared/NoContent';
import paths from '../../../config/local/paths';
import React from 'react';
import RegisteredFilter from '../../shared/Query/Filters/Registered/RegisteredFilter';
import SearchFilter from '../../shared/Query/Filters/Search/SearchFilter';
import SuccessOrWarningMessage from '../../../shared/Forms/Messages/SucessOrWarningMessage';

const EXPORT_FLAG_NAME = 'agent:export_contacts';

/** @extends {React.Component<Props, State>} */
export class ViewContacts extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		/**
		 * @type {State}
		 */
		this.state = {
			activeTab: 'contacts',
			contactList: [],
			currentPageNumber: 1,
			deleteRan: 0,
			errorMessage: null,
			filterQuery: this.#sort,
			hasPendingExport: false,
			hasResolvedPendingExport: false,
			isGettingData: true,
			lastPageNumber: 0,
			successOrWarningMessage:
				this.props.location?.state?.message ?? null,
			tableIsGettingData: true,
			totalContacts: 0,
		};

		this._handleExport = this._handleExport.bind(this);
		this._handleSubmitQuery = this._handleSubmitQuery.bind(this);
		this._resetTableAndTabs = this._resetTableAndTabs.bind(this);
		this._setCurrentPageNumber = this._setCurrentPageNumber.bind(this);
		this._setLastPageNumber = this._setLastPageNumber.bind(this);
		this._setTable = this._setTable.bind(this);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			const hasPendingExport = await this.#hasPendingExport();

			await this.#setInitialTable();

			this.setState(
				/**
				 * @param {State} prev
				 * @returns {State}
				 */
				(prev) => ({
					...prev,
					hasPendingExport,
					hasResolvedPendingExport: true,
					isGettingData: false,
				})
			);
			return undefined;
		} catch (error) {
			captureError(error);

			this.setState(
				/**
				 * @param {State} prev
				 * @returns {State}
				 */
				(prev) => ({
					...prev,
					errorMessage: translate('global.error'),
					isGettingData: false,
				})
			);

			return undefined;
		}
	}

	/**
	 * @returns {Promise<boolean>}
	 */
	async #hasPendingExport() {
		if (this.props.settings.features.get(EXPORT_FLAG_NAME)) {
			return this.props.exportService.hasPendingExport();
		}

		return true;
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #setInitialTable() {
		return this.#setAllContactsTable();
	}

	/**
	 * @param {Props} prevProps
	 * @param {State} prevState
	 * @returns {Promise<void>}
	 */
	// deepcode ignore ReactIncorrectReturnValue: False error, doesn't return a value
	async componentDidUpdate(prevProps, prevState) {
		if (this.state.currentPageNumber === prevState.currentPageNumber) {
			return;
		}

		await this._setTable();
	}

	/**
	 * @protected
	 * @returns {Promise<void>}
	 */
	async _resetTableAndTabs() {
		await this._setTable();

		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({
				...prev,
				deleteRan: prev.deleteRan + 1,
			})
		);
	}

	/**
	 * @protected
	 * @returns {Promise<void>}
	 */
	async _setTable() {
		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({
				...prev,
				successOrWarningMessage: null,
				tableIsGettingData: true,
			})
		);

		return this.#setAllContactsTable();
	}

	/**
	 * @protected
	 * @param {number} pageNumber
	 * @returns {void}
	 */
	_setCurrentPageNumber(pageNumber) {
		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({ ...prev, currentPageNumber: pageNumber })
		);
	}

	/**
	 * @protected
	 * @param {number} lastPageNumber
	 * @returns {void}
	 */
	_setLastPageNumber(lastPageNumber) {
		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({ ...prev, lastPageNumber })
		);
	}

	/**
	 * @returns {Promise<ContactsData>}
	 */
	async #getMyContactsData() {
		let filterQuery = {
			...this.state.filterQuery,
			'filter[office_id]': null,
		};

		if (this.props.auth.resolveSubscription()) {
			filterQuery = { ...filterQuery, ...this.#subscribedFilterQuery };
		}

		const data = await this.props.agentContactService.getContacts({
			...filterQuery,
			page: this.state.currentPageNumber,
		});

		return data;
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #setMyContactsTable() {
		const myContactsData = await this.#getMyContactsData();

		// The above uses current state
		this.setState((prev) => ({
			...prev,
			activeGroupName: null,
			contactList: myContactsData?.contacts,
			lastPageNumber: myContactsData?.totalPages,
			tableIsGettingData: false,
		}));
	}

	/**
	 * @returns {Promise<ContactsData>}
	 */
	async #getAllContactsData() {
		let filterQuery = { ...this.state.filterQuery };

		if (this.props.auth.resolveSubscription()) {
			filterQuery = { ...filterQuery, ...this.#subscribedFilterQuery };
		}

		const data = await this.props.agentContactService.getContacts({
			...filterQuery,
			page: this.state.currentPageNumber,
		});

		return data;
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #setAllContactsTable() {
		const allContactsData = await this.#getAllContactsData();

		// The above uses current state
		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({
				...prev,
				contactList: allContactsData.contacts,
				lastPageNumber: allContactsData?.totalPages,
				tableIsGettingData: false,
				totalContacts: allContactsData?.totalContacts ?? 0,
			})
		);
	}

	/**
	 * @param {FilterQueryParameters} value
	 * @returns {FilterQueryParameters}
	 */
	static #convertScoreToRange(value) {
		if (value['filter[score]'] === undefined) {
			return value;
		}
		// @ts-ignore
		if (value['filter[score]'].length === 0) {
			delete value['filter[score]'];

			return value;
		}

		/**
		 * @type {string[]}
		 */
		// @ts-ignore
		const valueList = value['filter[score]'] ?? [];
		delete value['filter[score]'];

		// Convert ranges to numbers
		const range = valueList
			.map((numbers) => numbers.split('-'))
			.flat()
			.map((number) => +number);
		/*
		 * If the array has only two or consecutive numbers it means we're within
		 * a range that's either a combination of the two selected ranges (e.g. if
		 * the user selected '80-100' and '40-79' we're within the range of 40 to 100)
		 * or only one selected range.
		 */
		if (range.length === 2 || arrayHasConsecutiveNumbers(range)) {
			// prettier-ignore
			value['range[lead_scores.0.score]'] = `${Math.min(...range)};${Math.max(...range)}`;
			value.compare = 'in';

			return value;
		}

		/*
		 * If the user has selected multiple options that do not have any
		 * consecutive numbers we're dealing with "Likely Movers" and
		 * "Unlikely Movers" having been selected. In that case we're dealing
		 * with the ranges of 80-100 and 0-39. Alternatively, we could say that
		 * we're querying anything that is OUTSIDE of 40-79. In order to convert
		 * these numbers we sort the array, remove the highest and lowest numbers,
		 * and then get the new highest and subtract one and the new lowest and
		 * add one. This will give us 40-79. We are not hardcoding numbers as
		 * these ranges are subject to change. So long as there remain 3 options
		 * this will work with any numbers.
		 */
		range.sort((a, b) => a - b);
		range.shift();
		range.pop();

		const top = Math.max(...range) - 1;
		const bottom = Math.min(...range) + 1;

		value['range[lead_scores.0.score]'] = `${bottom};${top}`;
		value.compare = 'out';

		return value;
	}

	/**
	 * @param {FilterQueryParameters} value
	 * @returns {FilterQueryParameters}
	 */
	static #convertFields(value) {
		return ViewContacts.#convertScoreToRange(value);
	}

	/**
	 * @protected
	 * @param {FilterQueryParameters} value
	 * @returns {FilterQueryParameters}
	 */
	_handleSubmitQuery(value) {
		this.setState(
			/**
			 * @param {State} prev
			 * @returns {State}
			 */
			(prev) => ({
				...prev,
				currentPageNumber: 1,
				filterQuery: ViewContacts.#convertFields({
					...prev.filterQuery,
					...value,
				}),
			}),
			() => {
				this._setTable();
			}
		);

		return value;
	}

	/** @returns {{ 'sort[lead_scores.0.score]': 'desc', 'sort[name.last]': 'asc', 'sort[name.first]': 'asc', }} */
	// eslint-disable-next-line class-methods-use-this
	get #subscribedFilterQuery() {
		return {
			'sort[lead_scores.0.score]': 'desc',
			'sort[name.last]': 'asc',
			// eslint-disable-next-line sort-keys
			'sort[name.first]': 'asc',
		};
	}

	/**
	 * @returns {{ 'sort[lead_scores.0.score]': 'desc', 'sort[name.last]': 'asc', 'sort[name.first]': 'asc', } | {}}
	 */
	get #sort() {
		if (!this?.props?.auth?.agentSubscriptionLevels?.length) {
			return {};
		}

		return this.#subscribedFilterQuery;
	}

	/**
	 * @protected
	 * @returns {Promise<void>}
	 */
	async _handleExport() {
		this.setState({
			hasPendingExport: true,
			hasResolvedPendingExport: true,
		});

		try {
			await this.props.exportService.createExport(
				'contacts',
				'agent',
				ViewContacts.#resolveExportQuery()
			);

			return undefined;
		} catch (error) {
			this.setState({
				errorMessage: translate('exports.errors.create'),
				hasPendingExport: false,
			});

			return undefined;
		}
	}

	/**
	 * @returns {QueryParameters}
	 */
	static #resolveExportQuery() {
		return {
			limit: undefined,
			page: undefined,
		};
	}

	/**
	 * @returns {boolean}
	 */
	#canExportContacts() {
		if (
			this.state.hasResolvedPendingExport &&
			this.state.contactList.length &&
			!this.state.hasPendingExport
		) {
			return true;
		}

		return false;
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderAddContactLinks() {
		return (
			<section className="button-group h-fit ml-auto">
				<LinkButton
					icon={images.icons.import}
					to={{
						pathname: paths.app.agent.contacts.import,
						state: {
							referrer: `${location.pathname}${location.search}${location.hash}`,
						},
					}}
				>
					{translate('agent.pages.contacts.view.import')}
				</LinkButton>
				<LinkButton
					icon={images.icons.add}
					to={{
						pathname: paths.app.agent.contacts.add,
						state: {
							referrer: `${location.pathname}${location.search}${location.hash}`,
						},
					}}
				>
					{translate('agent.pages.contacts.view.new')}
				</LinkButton>
				<Feature fallback={null} name={EXPORT_FLAG_NAME}>
					<BaseButton
						disabled={!this.#canExportContacts()}
						icon={images.icons.export}
						onClick={this._handleExport}
						outline={true}
					>
						{this.state.hasResolvedPendingExport &&
						this.state.hasPendingExport
							? translate('exports.export_pending')
							: translate('exports.export')}
					</BaseButton>
				</Feature>
			</section>
		);
	}

	/**
	 * @returns {boolean}
	 */
	#resolveMoverScore() {
		const hasPersonalTier = this.props.settings.isEnabled(
			flags.agent.mis,
			'agent_personal_tier'
		);
		const hasAgentTier = this.props.settings.isEnabled(
			flags.agent.mis,
			'agent_tier'
		);
		/** @type {import('../../../shared/SettingProvider/Component').UserTiers | {}} */
		const userTiers = this.props.settings.userTiers;
		const groupTiers = Object.keys(
			'agentGroupTiers' in userTiers ? userTiers.agentGroupTiers : {}
		);

		const hasGroupTier = groupTiers.some((groupId) =>
			this.props.settings.groupIsEnabled(flags.agent.mis, groupId)
		);

		return hasAgentTier || hasGroupTier || hasPersonalTier;
	}

	/**
	 * @returns {JSX.Element}
	 */
	/* eslint-disable class-methods-use-this -- Render method must be invokable by `this` */
	render() {
		if (this.state.errorMessage !== null && !this.state.isGettingData) {
			return <NoContent message={this.state.errorMessage} />;
		}

		return (
			<React.Fragment>
				<header className="mt-10 mb-6">
					<h1 className="hl-ms-6 mb-6">
						{`${translate('admin.pages.contacts.view.title')} (${
							this.state.totalContacts
						})`}
					</h1>

					<Feature fallback={null} name={EXPORT_FLAG_NAME}>
						<FormWarningMessages
							heading={translate('exports.started')}
							messages={
								this.state.hasResolvedPendingExport &&
								this.state.hasPendingExport
									? translate('exports.pending_description')
									: ''
							}
						/>
					</Feature>

					<div className="flex items-end align-bottom md:mb-0">
						{this.#renderAddContactLinks()}
					</div>
				</header>

				<SuccessOrWarningMessage
					heading={
						this.props.location?.state?.heading ??
						translate('agent.pages.contacts.view.success_header')
					}
					messages={this.state.successOrWarningMessage}
					warning={this.props.location?.state?.warning}
				/>

				<ContactsTable
					// @ts-ignore -- declared
					activeTab={this.state.activeTab}
					contactList={this.state.contactList}
					currentPageNumber={this.state.currentPageNumber}
					filters={
						<div className="flex gap-2 items-center flex-row-reverse">
							<RegisteredFilter
								onSubmit={this._handleSubmitQuery}
								query={this.state.filterQuery}
							/>
							<MoverScoreFilter
								gated={!this.#resolveMoverScore()}
								onSubmit={this._handleSubmitQuery}
								query={this.state.filterQuery}
							/>
							<SearchFilter
								onSubmit={this._handleSubmitQuery}
								query={this.state.filterQuery}
								search={[
									'address.street_address_1',
									'email',
									'name.first',
									'name.last',
									'phone_numbers.value',
									'phone.landline',
									'phone.mobile',
								]}
							/>
						</div>
					}
					isGettingData={this.state.tableIsGettingData}
					lastPageNumber={this.state.lastPageNumber}
					resetTableAndTabs={this._resetTableAndTabs}
					setCurrentPageNumber={this._setCurrentPageNumber}
				/>
			</React.Fragment>
		);
	}
}

// @ts-ignore
const ConnectedViewContacts =
	// @ts-ignore HOC typings from withAuth/service-container do not preserve this component's injected prop contract
	withAgentContactService(
		withSettings(withAuth(withExportService(ViewContacts)))
	);

export default ConnectedViewContacts;
